import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';

import TextInput from '../../components/TextInput';

import {
	requestKitShipment,
	requestKitShipmentReset
} from '../../containers/RequestKitShipment/requestKitShipmentSlice';

import helperImage from '../../assets/sub-num-help.jpeg';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material';

const StyledFormBox = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.background.default,
	padding: '25px'
}));

const StyledButton = styled(Button)(({ theme }) => ({
	width: 300,
	fontSize: '1.2em',
	fontWeight: 'bold',
	color: theme.palette.common.white
}));

const LinkButton = styled(Button)(({ theme }) => ({
	textTransform: 'none',
	padding: 0,
	margin: 0,
	color: 'inherit',
	textDecoration: 'underline'
}));

const defaultValues = {
	subscriberNumber: '',
	lastName: '',
	zipCode: ''
};

const Home = ({ t }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { handleSubmit, control, trigger, reset } = useForm({
		defaultValues
	});
	const [openSubNumberDialog, setOpenSubNumberDialog] = React.useState(false);

	const { data, error, hasCompleted } = useSelector(
		(state) => state.requestKitShipmentSlice.requestKitShipment
	);

	React.useEffect(() => {
		dispatch(requestKitShipmentReset());
		reset();
	}, []);

	React.useEffect(() => {
		if (!error && data && hasCompleted) {
			navigate('/shipmentinfo');
		}
	}, [error, data, hasCompleted]);

	const validateField = async (fieldName) => {
		try {
			await trigger(fieldName);
		} catch (err) {
			console.log('err', err);
		}
	};

	const onSubmit = (data, e) => {
		e.preventDefault();
		dispatch(
			requestKitShipment({
				subscriberNumber: data.subscriberNumber,
				lastName: data.lastName,
				zipCode: data.zipCode
			})
		);
	};
	return (
		<React.Fragment>
			<Typography
				component='h2'
				sx={{ fontSize: '2em', fontWeight: 'bold', marginBottom: '10px' }}
			>
				{t('home:welcomeHeader')}
			</Typography>
			<Typography component='p' sx={{ margin: '15px 0' }}>
				{t('home:welcomeText')}
			</Typography>
			<StyledFormBox component='section'>
				<Stack
					direction='row'
					flexWrap='wrap'
					justifyContent='space-between'
					spacing={2}
				>
					<Box sx={{ width: '45%' }}>
						<Typography
							component='p'
							sx={{ color: (theme) => theme.palette.error.main }}
						>
							* = {t('home:formElements.requiredField')}
						</Typography>
						{hasCompleted && !data?.response && (
							<Typography
								component='p'
								sx={{ color: (theme) => theme.palette.error.main }}
							>
								User does not exist
							</Typography>
						)}
						{error && (
							<Typography
								component='p'
								sx={{ color: (theme) => theme.palette.error.main }}
							>
								{error}
							</Typography>
						)}
						<TextInput
							rules={{
								required: { value: true, message: t('common:fieldRequired') }
							}}
							name='subscriberNumber'
							control={control}
							helperText={
								<LinkButton
									onClick={() => setOpenSubNumberDialog(true)}
									sx={{ fontSize: '12px' }}
								>
									{t('home:formElements.subMemberNumber-subText')}
								</LinkButton>
							}
							label={`${t('home:formElements.subMemberNumber')}*`}
							onChangeValidate={() => validateField('subscriberNumber')}
						/>

						<TextInput
							rules={{
								required: { value: true, message: t('common:fieldRequired') }
							}}
							name='lastName'
							control={control}
							label={`${t('home:formElements.lastName')}*`}
							onChangeValidate={() => validateField('lastName')}
						/>

						<TextInput
							rules={{
								required: { value: true, message: t('common:fieldRequired') },
								maxLength: 9
							}}
							name='zipCode'
							type='number'
							control={control}
							label={`${t('home:formElements.zipCode')}*`}
							onChangeValidate={() => validateField('zipCode')}
						/>
					</Box>

					<Box
						width='100%'
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flexWrap: 'wrap',
							alignItems: 'center'
						}}
					>
						<StyledButton
							variant='contained'
							color='success'
							size='large'
							onClick={handleSubmit(onSubmit)}
						>
							{t('common:buttons.submitRequest')}
						</StyledButton>
					</Box>
				</Stack>
			</StyledFormBox>
			<Typography sx={{ fontSize: '0.7em' }}>
				{t('home:disclosureText')}
			</Typography>
			<Dialog
				open={openSubNumberDialog}
				onClose={() => setOpenSubNumberDialog(false)}
				aria-labelledby='subscriber-number-help-title'
				aria-describedby='subscriber-number-help-description'
			>
				<DialogContent>
					<img src={helperImage} alt={t('home:subscriberHelperAltText')} />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenSubNumberDialog(false)}>
						{t('common:buttons.close')}
					</Button>
				</DialogActions>
			</Dialog>
		</React.Fragment>
	);
};

Home.propTypes = {
	t: PropTypes.func
};

export default withTranslation(['home', 'common'])(Home);
